/**
 * What the *system* asks to be spoken to in — `navigator.languages`, which is a platform global and
 * therefore R1's business rather than core's.
 *
 * Core ranks these against the catalogues this build has (`resolveLocale`); reading them is all that
 * happens here, and the two things it guards against are both real:
 *
 * - **`languages` is optional and `language` is not.** Every engine this app targets has the plural
 *   form, but a `<video>`-less test harness or an old WebView may not, and the fallback is one line.
 * - **It changes without a reload.** Chrome fires `languagechange` on the window when the OS or the
 *   browser's own language list is edited. Nothing here subscribes — the resolved locale is read once
 *   per page load, deliberately, because a language swap mid-render would re-run every memoised
 *   formatter and re-key nothing. The event is named here so the next reader knows it was considered.
 */
export function systemLocales(): readonly string[] {
  const nav: unknown = globalThis.navigator;
  if (typeof nav !== 'object' || nav === null) return [];

  const { languages, language } = nav as { languages?: unknown; language?: unknown };
  if (Array.isArray(languages)) {
    return languages.filter((tag): tag is string => typeof tag === 'string');
  }
  return typeof language === 'string' ? [language] : [];
}

/**
 * **What the document says it is written in** — `<html lang>`, which is the other half of choosing a
 * language and was missing until German shipped.
 *
 * `index.html` hardcodes `lang="en"` and nothing had reason to touch it while English was the only
 * catalogue. With a second one that attribute becomes a false statement, and not a cosmetic one: `lang`
 * is what picks a screen reader's voice and phoneme set, so a German page declaring English is read out
 * as German words pronounced as English ones. It also drives hyphenation and the quote glyphs
 * `:lang()`-aware CSS would choose.
 *
 * Set from the **UI** locale rather than the formatting one. The two differ exactly when the reader's
 * region is more specific than the copy (`de-AT` formatting over plain German words), and `lang`
 * describes the words. Claiming `de-AT` would assert an Austrian catalogue this build does not have.
 *
 * A DOM write, so it lives here rather than in `strings/index.ts`: that module is imported by
 * `core-testkit`'s `problem-copy.test.ts` under `node --test`, where there is no `document` at all.
 *
 * `document` is reached through `unknown` and narrowed by hand, the same way {@link systemLocales} reads
 * `navigator` — and for a sharper reason than style here. `core-testkit` compiles this file under a
 * tsconfig with **no DOM lib at all**, so `globalThis.document` is not merely absent at runtime, it does
 * not typecheck.
 */
export function applyDocumentLanguage(tag: string): void {
  const doc: unknown = (globalThis as { document?: unknown }).document;
  if (typeof doc !== 'object' || doc === null) return;

  const { documentElement } = doc as { documentElement?: unknown };
  if (typeof documentElement !== 'object' || documentElement === null) return;

  const { setAttribute } = documentElement as { setAttribute?: unknown };
  if (typeof setAttribute !== 'function') return;
  setAttribute.call(documentElement, 'lang', tag);
}
